import { minBy, uniqWith } from 'es-toolkit';

import { RoofContainer, SolidData } from '@paragon/contract-library';
import {
  angleLessThanOrEqual,
  Line2D,
  LineSegment2D,
  Point2D,
  Point3D,
  Polygon2D,
  Polygons,
} from '@paragon/geometry-library';
import { vertexLoopToPolygons2D } from '@paragon/geometry-library/source/scripts/polygonValidationUtilities';
import { circularPairwise, forceArray, notNullish } from '@paragon/utilities';

export function createRoofBasePolygons(source: RoofContainer): Polygon2D[] {
  if (!source.solidData) return [];
  return createRoofBasePolygonsFromSolidData(source.solidData);
}

export function createRoofBasePolygonsFromSolidData(solidData: SolidData): Polygon2D[] {
  const roofSolidEdgesProjectedTo2D = calculateEdgesProjectedTo2D(solidData);
  return createDisjointRoofBasePolygonFromEdges(roofSolidEdgesProjectedTo2D);
}

export function createRoofBasePolygonsFromPolygons(polygons: Polygons): Polygon2D[] {
  const polygonsEdgesProjectedTo2D = polygons.faces
    .flatMap((face) => face.edges)
    .map((edge) => {
      const basePoint = edge.basePoint.projectTo2D();
      const endPoint = edge.endPoint.projectTo2D();
      return basePoint.equals(endPoint) ? null : new LineSegment2D(basePoint, endPoint);
    })
    .filter(notNullish);
  return createDisjointRoofBasePolygonFromEdges(polygonsEdgesProjectedTo2D);
}

export function minimalHull(edges: readonly LineSegment2D[]) {
  if (edges.length === 0) return null;

  const splitEdges = LineSegment2D.splitAllSegmentsByEachOther(edges);

  const crossingLineBasePoint = splitEdges[0].midpoint;
  const crossingLineDirection = splitEdges[0].direction.rotateQuarterTurn();
  const crossingLine = new Line2D(crossingLineBasePoint, crossingLineDirection);
  const firstIntersection = minBy(
    splitEdges.map((segment) => segment.intersectWithLine(crossingLine)).filter(notNullish),
    (intersection) => intersection.signedDistanceAlong(crossingLine),
  )!;
  const firstSegmentCandidates = splitEdges.filter((segment) => segment.contains(firstIntersection));
  let firstSegment = firstSegmentCandidates[0];
  if (firstSegmentCandidates.length > 1) {
    firstSegment = minBy(firstSegmentCandidates, (segment) => {
      const segmentDirectionAwayFromLine = crossingLine.containsPoint(segment.basePoint)
        ? segment.direction
        : segment.direction.reverse();
      return crossingLineDirection.dotProduct(segmentDirectionAwayFromLine);
    })!;
  }

  let [currentBasePoint, currentEndPoint] =
    crossingLineDirection.rotateQuarterTurn().dotProduct(firstSegment.direction) > 0
      ? [firstSegment.endPoint, firstSegment.basePoint]
      : [firstSegment.basePoint, firstSegment.endPoint];

  const getNextPointAndBase = (segments: LineSegment2D[], previous: Point2D, current: Point2D) =>
    minBy(
      segments
        .map((segment) =>
          segment.basePoint.equals(current) && !segment.endPoint.equals(current)
            ? { nextPoint: segment.endPoint, basePoint: segment.basePoint }
            : segment.endPoint.equals(current) && !segment.basePoint.equals(current)
              ? { nextPoint: segment.basePoint, basePoint: segment.endPoint }
              : null,
        )
        .filter(notNullish),
      ({ nextPoint }) => {
        const legBackward = current.directionToPoint(previous);
        const legForward = current.directionToPoint(nextPoint);
        const angle = legBackward.angleTo(legForward);
        // Backtracking (angleEquals(angle, 0)) is permissible, but should be the least priority.
        return angleLessThanOrEqual(angle, 0) ? angle + 360 : angle;
      },
    );

  // Walk along the outside of the graph
  const vertices = [currentBasePoint, currentEndPoint];
  let i = 1;
  const iterationLimit = splitEdges.length * 2; // At most each segment can be traversed twice, once in each direction.
  for (; i < iterationLimit; i++) {
    const step = getNextPointAndBase(splitEdges, currentBasePoint, currentEndPoint);
    if (!step) return null;
    if (step.nextPoint.equals(vertices[0])) break;
    vertices.push(step.nextPoint);
    // Carry the chosen segment's own endpoint forward (not `current`, which may be a
    // near-miss duplicate within tolerance) so the next backtracking direction is exact.
    [currentBasePoint, currentEndPoint] = [step.basePoint, step.nextPoint];
  }
  if (i === iterationLimit) return null;
  return vertices;
}

export function createDisjointRoofBasePolygonFromEdges(edges: readonly LineSegment2D[]) {
  const vertices = minimalHull(edges);
  if (!vertices) return [];
  return vertexLoopToPolygons2D(vertices) ?? [];
}

export function findNonBoundaryEdgesProjectedTo2D(solidData: SolidData, roofBasePolygon: readonly Polygon2D[]) {
  const polygons = forceArray(roofBasePolygon);
  const roofBasePolygonEdges = polygons.flatMap(({ edges }) => edges);
  const roofSolidEdgesProjectedTo2D = calculateEdgesProjectedTo2D(solidData);
  return roofSolidEdgesProjectedTo2D.filter(
    (roofSolidEdge) => !roofBasePolygonEdges.some((roofBaseEdge) => roofBaseEdge.equals(roofSolidEdge)),
  );
}

export function calculateEdgesProjectedTo2D(solidData: SolidData) {
  return calculateEdges(solidData)
    .map((edge) => {
      const vertex1 = edge[0].projectTo2D();
      const vertex2 = edge[1].projectTo2D();
      return vertex1.equals(vertex2) ? null : new LineSegment2D(vertex1, vertex2);
    })
    .filter(notNullish);
}

export function calculateEdges(solidData: SolidData) {
  const edgesByVertexIndices = uniqWith(
    solidData.faces.flatMap((face) => [face.outer, ...face.inner]).flatMap(circularPairwise),
    edgeEquals,
  );
  return edgesByVertexIndices.map((edge): [Point3D, Point3D] => [
    Point3D.fromObject(solidData.vertices[edge[0]]),
    Point3D.fromObject(solidData.vertices[edge[1]]),
  ]);

  /**
   * Each edge is represented by vertex indices.
   */
  function edgeEquals(edge1: readonly [number, number], edge2: readonly [number, number]) {
    return (edge1[0] === edge2[0] && edge1[1] === edge2[1]) || (edge1[0] === edge2[1] && edge1[1] === edge2[0]);
  }
}
